import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Chip, GapChip, Tooltip } from '../ui';
import { formatMoneyShort } from '../../utils/lotValue';
import {
    inventoryLink, mixScale, outlierTags, sortBranchRows,
} from '../../utils/branchView';
import InventoryLink from './InventoryLink';
import { DEFAULT_SETTINGS } from '../../utils/dealershipSettings';
import { useDealershipSettings } from '../../contexts/settingsContext';

const pctOfFraction = (f) => `${Number((f * 100).toFixed(1))}%`;

// The wording follows the dealership's settings: `oldest` is the last aging
// boundary (180 by default), `t` the gap scale (fractions).
export const standOutTip = (oldest, t) => `Branches with a red flag on the share past ${oldest} days or high sell-speed risk (against the dealership), asking more than ${pctOfFraction(t.critical)} over AI, or priced more than ${pctOfFraction(t.warn)} under AI`;
export const STAND_OUT_TIP = standOutTip(DEFAULT_SETTINGS.aging.boundaries[2], DEFAULT_SETTINGS.gapThresholds);

// Sortable columns; the first click sorts names A→Z and numbers high→low.
const comparisonColumns = (oldest, t) => [
    { key: 'name', label: 'Branch' },
    { key: 'units', label: 'Units', r: true },
    { key: 'totalValue', label: 'Listing value', r: true },
    { key: 'agedPct', label: `Past ${oldest} days`, r: true, tip: `Share of units in stock longer than ${oldest} days. The tick marks the dealership.` },
    { key: 'riskPct', label: 'High sell-speed risk', r: true, tip: 'High-risk share of the units with a sell-speed risk score. The tick marks the dealership.' },
    { key: 'gapPct', label: 'Asking vs AI', r: true, tip: `Used units: asking total against the AI value total. Same scale as the inventory AI chip: over +${pctOfFraction(t.critical)} red, +${pctOfFraction(t.warn).replace('%', '')} to +${pctOfFraction(t.critical)} amber, under −${pctOfFraction(t.warn)} teal.` },
    { key: 'activePct', label: 'Active interest', r: true, tip: 'Hot or warm units (quoted in the last 30 days).' },
    { key: 'avgDays', label: 'Avg days', r: true },
];

const FILL_TONE = { crit: 'crit', warn: 'warn', ok: 'ok' };

/** Share bar with the dealership tick; the number carries the meaning. */
function ShareBar({ value, dealer, max, flag }) {
    const tone = FILL_TONE[flag] || 'ok';
    return (
        <span className="br2-mb">
            <span className="br2-track" aria-hidden="true">
                <span className={`br2-fill br2-fill--${tone}`} style={{ width: `${Math.min(100, (value / max) * 100)}%` }} />
                {dealer != null && <span className="br2-avg" style={{ left: `${Math.min(100, (dealer / max) * 100)}%` }} />}
            </span>
            <span className={`br2-mb-v br2-ink-${tone}`}>{value.toFixed(0)}%</span>
        </span>
    );
}

ShareBar.propTypes = {
    value: PropTypes.number.isRequired, dealer: PropTypes.number, max: PropTypes.number.isRequired, flag: PropTypes.string,
};

/** Diverging asking-vs-AI bar on one symmetric scale (decorative). */
export function GapBar({ gap, scale, tone }) {
    const width = gap == null || !scale ? 0 : Math.min(Math.abs(gap) / scale, 1) * 50;
    return (
        <span className="br2-dv" aria-hidden="true">
            <span className="br2-dv-zero" />
            <span className={`br2-dv-bar br2-dv-bar--${tone} ${gap < 0 ? 'is-neg' : 'is-pos'}`} style={{ width: `${width}%` }} />
        </span>
    );
}

GapBar.propTypes = { gap: PropTypes.number, scale: PropTypes.number, tone: PropTypes.string };

const seg = (tone, value, total) => (value > 0 && total > 0
    ? <span key={tone} className={`v2-tone-${tone}`} style={{ width: `${(value / total) * 100}%` }} />
    : null);

/**
 * Today's per-branch mini-bars (P-BR-031..033): aging, buyer interest and,
 * when scored, sell-speed risk. Decorative; a hidden sentence reads them out.
 */
export function MixBars({ branch: b }) {
    const { aging } = useDealershipSettings();
    const [b1, b2, b3] = aging.boundaries;
    const scored = (b.risk_high || 0) + (b.risk_medium || 0) + (b.risk_low || 0);
    const text = `Aging: ${b.fresh} up to ${b1} days, ${b.normal} ${b1 + 1}–${b2}, ${b.slow} ${b2 + 1}–${b3}, ${b.critical} over ${b3}. `
        + `Interest: ${b.hot} hot, ${b.warm} warm, ${b.cold} cold, ${b.no_interest} none.`
        + (scored > 0 ? ` Risk: ${b.risk_high} high, ${b.risk_medium} medium, ${b.risk_low} low.` : '');
    return (
        <span className="br2-mix" data-testid="mix">
            <span className="br2-mini" aria-hidden="true" data-bar="aging">
                {seg('sage', b.fresh, b.total)}{seg('slate', b.normal, b.total)}{seg('amber', b.slow, b.total)}{seg('red', b.critical, b.total)}
            </span>
            <span className="br2-mini" aria-hidden="true" data-bar="interest">
                {seg('red', b.hot, b.total)}{seg('amber', b.warm, b.total)}{seg('slate', b.cold, b.total)}{seg('none', b.no_interest, b.total)}
            </span>
            {scored > 0 && (
                <span className="br2-mini" aria-hidden="true" data-bar="risk">
                    {seg('red', b.risk_high, scored)}{seg('amber', b.risk_medium, scored)}{seg('sage', b.risk_low, scored)}
                </span>
            )}
            <span className="v2-sr">{text}</span>
        </span>
    );
}

MixBars.propTypes = { branch: PropTypes.object.isRequired };

/**
 * B3: the branch comparison, which is also the branch picker (tick a row, or
 * click it; "Only" keeps just that branch). Every column sorts; rows without
 * a value sort last. Red tags mark branches far from the dealership (D15);
 * "Stand out" keeps only those. The Dealership row is the reference.
 */
export default function ComparisonTable({
    rows, dealership, selected, allSelected, onToggle, onSelectAll, onClear, onOnly,
    standOutOnly, onToggleStandOut, allNames,
}) {
    const [sort, setSort] = useState({ key: 'units', dir: 'desc' });
    const { aging, gapThresholds } = useDealershipSettings();
    const COLUMNS = comparisonColumns(aging.boundaries[2], gapThresholds);
    const headerBox = useRef(null);
    const none = selected.size === 0;

    useEffect(() => {
        if (headerBox.current) headerBox.current.indeterminate = !allSelected && !none;
    }, [allSelected, none]);

    const sorted = useMemo(() => sortBranchRows(rows, sort), [rows, sort]);
    const shown = standOutOnly ? sorted.filter((r) => r.standOut) : sorted;
    const standOutCount = rows.filter((r) => r.standOut).length;

    const agedMax = mixScale([...rows.map((r) => r.agedPct), dealership?.agedPct || 0]);
    const riskMax = mixScale([...rows.map((r) => r.riskPct), dealership?.riskPct || 0]);
    const gapScale = Math.max(0, ...rows.map((r) => (r.gapPct == null ? 0 : Math.abs(r.gapPct))), Math.abs(dealership?.gapPct || 0));

    const onSort = (key) => setSort((prev) => (prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'name' ? 'asc' : 'desc' }));

    const rowClick = (name) => (e) => {
        if (e.target.closest('button, a, input')) return;
        onToggle(name);
    };

    return (
        <section className="v2-board br2-board" aria-labelledby="br2-cmp-h">
            <div className="br2-card-h">
                <h2 id="br2-cmp-h">Branch comparison</h2>
                <span className="br2-sub"><b>Choose branches here:</b> tick rows or use Only. Everything below shows your selection. Ticks on the bars mark the dealership.</span>
                <span className="br2-spacer" />
                <Tooltip content={standOutTip(aging.boundaries[2], gapThresholds)}>
                    <button
                        type="button"
                        className="br2-chip-t br2-chip-t--crit"
                        aria-pressed={standOutOnly}
                        onClick={onToggleStandOut}
                        data-testid="stand-out"
                    >
                        <i className="v2-tone-red" aria-hidden="true" />
                        Stand out
                        <span className="br2-chip-c">{standOutCount}</span>
                    </button>
                </Tooltip>
            </div>
            <div className="v2-tscroll br2-tscroll">
                <table className="v2-table br2-matrix" aria-label="Branch comparison">
                    <thead>
                        <tr>
                            <th className="br2-cb" scope="col">
                                <span>
                                    <input
                                        ref={headerBox}
                                        type="checkbox"
                                        aria-label="Select all branches"
                                        checked={allSelected}
                                        onChange={() => (allSelected ? onClear() : onSelectAll())}
                                    />
                                </span>
                            </th>
                            {COLUMNS.map((c) => {
                                const on = sort.key === c.key;
                                const ariaSort = on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined;
                                return (
                                    <th key={c.key} scope="col" className={c.r ? 'v2-r' : undefined} aria-sort={ariaSort}>
                                        <span className="v2-th-split">
                                            <button type="button" onClick={() => onSort(c.key)}>
                                                {c.label}
                                                <span className="v2-ar" aria-hidden="true">{on ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
                                            </button>
                                            {c.tip && <Tooltip label={`About ${c.label}`} content={c.tip} />}
                                        </span>
                                    </th>
                                );
                            })}
                            <th scope="col">
                                <span className="v2-th-split">
                                    <span className="br2-th-plain">Mix</span>
                                    <Tooltip label="About Mix" content="Aging, buyer interest and (when scored) sell-speed risk of each branch, as on today's branch list." />
                                </span>
                            </th>
                            <th scope="col"><span className="v2-sr">Open</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map((r) => {
                            const on = selected.has(r.name);
                            const tags = outlierTags(r.flags);
                            return (
                                <tr key={r.name} aria-selected={on} className="br2-tr" onClick={rowClick(r.name)} data-branch={r.name}>
                                    <td className="br2-cb">
                                        <input type="checkbox" aria-label={`Select ${r.name}`} checked={on} onChange={() => onToggle(r.name)} />
                                    </td>
                                    <td className="br2-namec">
                                        <span className="br2-bname">
                                            <b title={r.name}>{r.name}</b>
                                            {tags.length > 0 && (
                                                <span className="br2-flags">
                                                    {tags.map((t) => <Chip key={t.id} tone={t.tone} className="br2-tag">{t.label}</Chip>)}
                                                </span>
                                            )}
                                            <button type="button" className="br2-only" aria-label={`Select only ${r.name}`} onClick={() => onOnly(r.name)}>Only</button>
                                        </span>
                                    </td>
                                    <td className="v2-r br2-num">{r.units.toLocaleString('en-US')}</td>
                                    <td className="v2-r br2-num">{r.totalValue == null ? '—' : formatMoneyShort(r.totalValue)}</td>
                                    <td className="v2-r"><ShareBar value={r.agedPct} dealer={dealership?.agedPct} max={agedMax} flag={r.flags.aged} /></td>
                                    <td className="v2-r"><ShareBar value={r.riskPct} dealer={dealership?.riskPct} max={riskMax} flag={r.flags.slow} /></td>
                                    <td className="v2-r">
                                        <span className="br2-gp">
                                            <GapBar gap={r.gapPct} scale={gapScale} tone={r.flags.gap} />
                                            <GapChip gap={r.gapPct} />
                                        </span>
                                    </td>
                                    <td className="v2-r br2-num">{r.activePct.toFixed(0)}%</td>
                                    <td className="v2-r br2-num">{r.avgDays == null ? '—' : Math.round(r.avgDays)}</td>
                                    <td><MixBars branch={r.branch} /></td>
                                    <td className="br2-act">
                                        <InventoryLink
                                            to={inventoryLink({ names: [r.name], allNames })}
                                            className="v2-ib br2-go"
                                            aria-label={`Open ${r.name} inventory`}
                                            title={`Open ${r.name} inventory`}
                                        >
                                            <span aria-hidden="true">›</span>
                                        </InventoryLink>
                                    </td>
                                </tr>
                            );
                        })}
                        {shown.length === 0 && (
                            <tr>
                                <td colSpan={COLUMNS.length + 3}>
                                    <p className="br2-empty-row">No branch stands out against the dealership.</p>
                                </td>
                            </tr>
                        )}
                        {dealership && (
                            <tr className="br2-total" data-testid="dealership-row">
                                <td className="br2-cb" />
                                <td>Dealership</td>
                                <td className="v2-r br2-num">{dealership.units.toLocaleString('en-US')}</td>
                                <td className="v2-r br2-num">{dealership.totalValue == null ? '—' : formatMoneyShort(dealership.totalValue)}</td>
                                <td className="v2-r br2-num">{dealership.agedPct.toFixed(0)}%</td>
                                <td className="v2-r br2-num">{dealership.riskPct.toFixed(0)}%</td>
                                <td className="v2-r">
                                    <span className="br2-gp">
                                        <GapBar gap={dealership.gapPct} scale={gapScale} tone="ref" />
                                        <GapChip gap={dealership.gapPct} />
                                    </span>
                                </td>
                                <td className="v2-r br2-num">{dealership.activePct.toFixed(0)}%</td>
                                <td className="v2-r br2-num">{Math.round(dealership.avgDays || 0)}</td>
                                <td />
                                <td className="br2-act" />
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="br2-foot">
                <span className="br2-legend">
                    <span><i className="br2-dot v2-tone-red" aria-hidden="true" />Red: far from the dealership</span>
                    <span><i className="br2-dot v2-tone-amber" aria-hidden="true" />Amber: watch</span>
                    <span><i className="br2-dot v2-tone-teal" aria-hidden="true" />Teal: priced under AI</span>
                </span>
            </div>
        </section>
    );
}

ComparisonTable.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    dealership: PropTypes.object,
    selected: PropTypes.instanceOf(Set).isRequired,
    allSelected: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
    onSelectAll: PropTypes.func.isRequired,
    onClear: PropTypes.func.isRequired,
    onOnly: PropTypes.func.isRequired,
    standOutOnly: PropTypes.bool.isRequired,
    onToggleStandOut: PropTypes.func.isRequired,
    allNames: PropTypes.arrayOf(PropTypes.string).isRequired,
};

